import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { 
  Carousel, 
  CarouselContent, 
  CarouselItem, 
  CarouselNext, 
  CarouselPrevious 
} from "@/components/ui/carousel";
import { Button } from "@/components/ui/button";
import { 
  ChevronLeft, 
  ChevronRight, 
  ChevronDown, 
  Home, 
  Maximize, 
  Minimize, 
  Presentation, 
  Users, 
  Sparkles, 
  ArrowRight 
} from "lucide-react";
import { PeptideStructure } from "@/assets/diagrams/peptide-structure";
import { CellSignaling } from "@/assets/diagrams/cell-signaling";
import KeyTakeaway from "@/components/ui/key-takeaway";

export default function Slideshow() {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [, setLocation] = useLocation();
  const [carouselApi, setCarouselApi] = useState<any>(null);
  
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    }
  };
  
  // Handle navigation with arrow keys
  const handleNextSlide = () => {
    if (carouselApi) {
      carouselApi.scrollNext();
    }
  };
  
  const handlePrevSlide = () => {
    if (carouselApi) {
      carouselApi.scrollPrev();
    }
  };

  // Handle keydown events for navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "f" || e.key === "F") {
        toggleFullscreen();
      } else if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        handleNextSlide();
      } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        handlePrevSlide();
      }
      // Escape key will be handled by browser to exit fullscreen
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [carouselApi]);

  // Collection of slide contents - in the exact same order as the Home page
  const slides = [
    // Hero slide
    {
      title: "Peptides: The Language of Cells",
      content: (
        <div className="text-center">
          <div className="mb-4">
            <span className="inline-block text-lg md:text-xl font-bold text-[#61DBFB] mb-2">NEXT GENERATION MEDICINE</span>
            <h1 className="font-serif text-4xl md:text-6xl font-bold">
              Peptides: The Language of Cells and the Future of Medicine
            </h1>
          </div>
          <p className="text-xl md:text-2xl opacity-90 mb-8">
            How tiny protein fragments are revolutionizing our approach to health and longevity
          </p>
          <div className="flex flex-col md:flex-row items-center justify-center gap-4">
            <Button 
              className="inline-flex items-center bg-white text-[#2C3E50] font-bold py-3 px-6 rounded-full shadow-lg hover:bg-[#ECF0F1] transition-colors"
            >
              Explore Free Content
              <ChevronDown className="ml-2 h-5 w-5" />
            </Button>
            
            <Button 
              className="inline-flex items-center bg-[#3498DB] text-white font-bold py-3 px-6 rounded-full shadow-lg hover:bg-[#2980B9] transition-colors"
            >
              <Users className="mr-2 h-5 w-5" />
              Join Membership
            </Button>
          </div>
        </div>
      )
    },
    
    // Introduction slide
    {
      title: "Introduction",
      content: (
        <div>
          <div className="border-l-4 border-[#E74C3C] px-6 py-4 bg-[#ECF0F1] rounded mb-8">
            <p className="text-lg italic">
              Peptides represent one of the most promising frontiers in medicine today. 
              Operating as the body's natural signaling molecules, they offer precise 
              biological effects with minimal side effects. From diabetes treatment to 
              weight management and beyond, peptides are poised to transform how we 
              approach health and aging.
            </p>
          </div>
          
          <p className="text-lg leading-relaxed mb-6">
            In this feature, we'll explore the fascinating world of peptides—from their 
            basic biological function to their emerging applications in modern medicine. 
            You'll discover why these tiny protein fragments are capturing the attention 
            of physicians, researchers, and health-conscious individuals alike.
          </p>
          
          <KeyTakeaway>
            Peptides occupy a unique middle ground in therapeutics: larger than small-molecule 
            drugs but smaller than antibodies. This "Goldilocks zone" allows them to engage 
            targets too large for conventional drugs while avoiding the complexity and cost 
            of full-sized biologics.
          </KeyTakeaway>
        </div>
      )
    },
    
    // What Are Peptides - Biological Basis - Part 1
    {
      title: "Biological Basis of Peptides (1/4)",
      content: (
        <div>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#2C3E50] mb-8 pb-2 border-b-2 border-[#ECF0F1]">
            Biological Basis of Peptides
          </h2>
          
          <div className="flex flex-col md:flex-row gap-8">
            <div className="w-full">
              <h3 className="font-serif text-2xl font-bold text-[#2C3E50] mb-4">
                What Are Peptides?
              </h3>
              <p className="mb-4">
                Peptides are short chains of amino acids—generally 2 to 50 units long—that serve 
                as the building blocks of proteins. They occur naturally throughout the body as 
                hormones, neurotransmitters, growth factors, and signaling molecules.
              </p>
              
              <div className="bg-[#F8F9FA] p-4 border-l-4 border-[#3498DB] mb-6">
                <h4 className="font-bold text-[#2C3E50] mb-2">Understanding Peptide Structure</h4>
                <p className="mb-4">Peptides consist of amino acids joined by peptide bonds. Their structure determines their function:</p>
                <ul className="list-disc pl-5 mb-4">
                  <li>Primary structure: The sequence of amino acids</li>
                  <li>Secondary structure: Local folding patterns (helices, sheets)</li>
                  <li>Tertiary structure: Overall three-dimensional shape</li>
                </ul>
                <p>This structure allows peptides to bind with specific receptors on cell surfaces, initiating biochemical reactions inside the cell.</p>
              </div>
              
              <KeyTakeaway>
                Peptides are essentially small proteins – usually defined as chains of 50 or fewer amino acids.
                They play diverse roles in biology as hormones, neurotransmitters, and signaling molecules.
              </KeyTakeaway>
            </div>
          </div>
        </div>
      )
    },
    
    // What Are Peptides - Biological Basis - Part 2 
    {
      title: "Biological Basis of Peptides (2/4)",
      content: (
        <div>
          <div className="flex flex-col md:flex-row gap-8">
            <div className="md:w-1/2">
              <p className="mb-4">
                Think of peptides as the "words" in the language of cellular communication. 
                Each peptide delivers specific instructions to cells, triggering cascades of 
                intracellular events. This communication network keeps our bodies functioning 
                properly—regulating everything from metabolism and immune response to tissue 
                repair and hormone production.
              </p>
              
              <p>
                Unlike many pharmaceuticals that work by blocking biological processes, peptides 
                typically enhance or modulate natural functions. This biomimetic approach is a key 
                reason why peptides often produce fewer side effects than conventional drugs.
              </p>
            </div>
            
            <div className="md:w-1/2">
              <div className="rounded-lg overflow-hidden shadow-lg mb-6">
                <div className="h-64 bg-[#EBF5FB] flex items-center justify-center p-4">
                  <PeptideStructure />
                </div>
                <div className="p-4 bg-white">
                  <p className="text-sm text-[#7F8C8D]">
                    Visualization of a peptide's molecular structure, showing the chain of amino acids linked by peptide bonds.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-lg overflow-hidden mt-6">
            <div className="p-6">
              <h4 className="font-bold text-[#2C3E50] mb-3">Key Characteristics of Peptides</h4>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <div className="h-5 w-5 mr-2 text-[#3498DB] flex-shrink-0 mt-0.5 flex items-center justify-center rounded-full border border-[#3498DB]">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span><strong>Precision:</strong> High specificity for their cellular targets</span>
                </li>
                <li className="flex items-start">
                  <div className="h-5 w-5 mr-2 text-[#3498DB] flex-shrink-0 mt-0.5 flex items-center justify-center rounded-full border border-[#3498DB]">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span><strong>Low Toxicity:</strong> Metabolize into amino acids with minimal waste</span>
                </li>
                <li className="flex items-start">
                  <div className="h-5 w-5 mr-2 text-[#3498DB] flex-shrink-0 mt-0.5 flex items-center justify-center rounded-full border border-[#3498DB]">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span><strong>Regulatory:</strong> Modulate rather than block biological processes</span>
                </li>
                <li className="flex items-start">
                  <div className="h-5 w-5 mr-2 text-[#3498DB] flex-shrink-0 mt-0.5 flex items-center justify-center rounded-full border border-[#3498DB]">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span><strong>Diversity:</strong> Can be engineered for various therapeutic applications</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      )
    },
    
    // What Are Peptides - Biological Basis - Part 3
    {
      title: "Biological Basis of Peptides (3/4)",
      content: (
        <div>
          {/* Interactive Cell Signaling Diagram */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="p-6">
              <h3 className="font-serif text-2xl font-bold text-[#2C3E50] mb-4">
                Cellular "Language" of Signaling
              </h3>
              <p className="mb-6">
                Peptides function as messengers in the body's complex communication network. 
                When a peptide binds to a receptor on a cell's surface, it triggers a series 
                of biochemical reactions that alter the cell's behavior.
              </p>
              
              <div className="relative aspect-video bg-[#EBF5FB] rounded-lg flex justify-center items-center mb-6">
                <CellSignaling />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                <div className="bg-[#EBF5FB] p-4 rounded-lg">
                  <h4 className="font-bold text-[#2C3E50] mb-2">1. Binding</h4>
                  <p className="text-sm">Peptide attaches to specific receptor on cell surface</p>
                </div>
                <div className="bg-[#EBF5FB] p-4 rounded-lg">
                  <h4 className="font-bold text-[#2C3E50] mb-2">2. Signaling</h4>
                  <p className="text-sm">Receptor changes shape, triggering internal cell messengers</p>
                </div>
                <div className="bg-[#EBF5FB] p-4 rounded-lg">
                  <h4 className="font-bold text-[#2C3E50] mb-2">3. Response</h4>
                  <p className="text-sm">Cell alters its behavior based on the peptide's "message"</p>
                </div>
              </div>
              
              <KeyTakeaway>
                Peptides "speak the language" of cell signaling by mimicking the body's own regulatory peptides. 
                This allows them to modulate biological pathways in a finely tuned way, turning processes up or down 
                rather than crudely shutting them down.
              </KeyTakeaway>
            </div>
          </div>
        </div>
      )
    },
    
    // What Are Peptides - Biological Basis - Part 4
    {
      title: "Biological Basis of Peptides (4/4)",
      content: (
        <div>
          <div className="bg-[#ECF0F1] p-6 rounded-lg">
            <h3 className="font-serif text-xl font-bold text-[#2C3E50] mb-3">Why This Matters</h3>
            <p className="mb-4">
              By understanding peptides as cellular messengers, scientists can develop targeted 
              treatments that work harmoniously with the body's natural systems. This represents 
              a fundamental shift from traditional pharmaceuticals that often disrupt normal 
              biological processes.
            </p>
            <p className="mb-4">
              One caveat is that natural peptides are often quickly degraded in the body, a challenge 
              for therapeutic development. Modern peptide engineering techniques focus on 
              extending half-life while preserving biological activity, creating more stable and 
              effective therapeutic peptides.
            </p>
            
            <KeyTakeaway>
              Because peptides are modeled after natural signals, they act with high specificity and fit their 
              targets like a key in a lock. This precision, combined with their natural degradation into amino acids,
              often results in fewer side effects compared to conventional drugs.
            </KeyTakeaway>
          </div>
        </div>
      )
    },
    
    // Peptides vs. Conventional Pharmaceuticals - Part 1
    {
      title: "Peptides vs. Conventional Pharmaceuticals (1/2)",
      content: (
        <div>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#2C3E50] mb-8 pb-2 border-b-2 border-[#ECF0F1]">
            Peptides vs. Conventional Pharmaceuticals
          </h2>
          
          <p className="text-lg mb-8">
            Traditional pharmaceuticals and peptide therapeutics represent fundamentally 
            different approaches to medicine. Understanding these differences helps explain 
            why peptides are generating such excitement in medical research and clinical practice.
          </p>
          
          {/* Interactive Comparison Tool */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-10">
            <div className="bg-[#2C3E50] text-white p-4">
              <h3 className="font-serif text-xl font-semibold">
                Comparison: "Blunt Force" vs. "Biomimetic" Action
              </h3>
            </div>
            
            <div className="p-6 overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-[#EBF5FB]">
                    <th className="py-3 px-4 text-left border-b-2 border-[#3498DB]">Characteristic</th>
                    <th className="py-3 px-4 text-left border-b-2 border-[#3498DB]">Conventional Pharmaceuticals</th>
                    <th className="py-3 px-4 text-left border-b-2 border-[#3498DB]">Peptide Therapeutics</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="even:bg-gray-50">
                    <td className="py-3 px-4 border-b border-gray-200 font-semibold">Mode of Action</td>
                    <td className="py-3 px-4 border-b border-gray-200">Often block receptors or inhibit enzymes; "off-switch" approach</td>
                    <td className="py-3 px-4 border-b border-gray-200">Mimic natural signals; modulate pathways up or down</td>
                  </tr>
                  <tr className="even:bg-gray-50">
                    <td className="py-3 px-4 border-b border-gray-200 font-semibold">Target Specificity</td>
                    <td className="py-3 px-4 border-b border-gray-200">May bind to multiple targets ("shotgun" approach)</td>
                    <td className="py-3 px-4 border-b border-gray-200">High specificity for intended targets ("key in lock")</td>
                  </tr>
                  <tr className="even:bg-gray-50">
                    <td className="py-3 px-4 border-b border-gray-200 font-semibold">Side Effects</td>
                    <td className="py-3 px-4 border-b border-gray-200">Often substantial due to off-target binding</td>
                    <td className="py-3 px-4 border-b border-gray-200">Typically fewer due to high specificity</td>
                  </tr>
                  <tr className="even:bg-gray-50">
                    <td className="py-3 px-4 border-b border-gray-200 font-semibold">Relationship to Biology</td>
                    <td className="py-3 px-4 border-b border-gray-200">Introduce novel chemical structures</td>
                    <td className="py-3 px-4 border-b border-gray-200">Use/mimic the body's own molecules</td>
                  </tr>
                  <tr className="even:bg-gray-50">
                    <td className="py-3 px-4 border-b border-gray-200 font-semibold">Metabolic Fate</td>
                    <td className="py-3 px-4 border-b border-gray-200">Often produce metabolites requiring liver processing</td>
                    <td className="py-3 px-4 border-b border-gray-200">Typically break down into amino acids</td>
                  </tr>
                  <tr className="even:bg-gray-50">
                    <td className="py-3 px-4 font-semibold">Response Type</td>
                    <td className="py-3 px-4">Linear dose-response; may override feedback loops</td>
                    <td className="py-3 px-4">Often produce transient, tunable responses</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )
    },
    
    // Peptides vs. Conventional Pharmaceuticals - Part 2
    {
      title: "Peptides vs. Conventional Pharmaceuticals (2/2)",
      content: (
        <div>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#2C3E50] mb-8 pb-2 border-b-2 border-[#ECF0F1]">
            Peptides vs. Conventional Pharmaceuticals
          </h2>
          
          {/* Visual Comparison */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="bg-[#7F8C8D] p-3">
                <h4 className="font-serif text-lg font-semibold text-white">Conventional Pharmaceutical</h4>
              </div>
              <div className="p-6">
                <div className="flex justify-center mb-6">
                  <svg className="h-48 w-full" viewBox="0 0 300 200">
                    <rect x="70" y="50" width="160" height="100" rx="10" fill="#f5f5f5" stroke="#d1d1d1" strokeWidth="2"/>
                    <circle cx="150" cy="100" r="30" fill="#E74C3C"/>
                    <path d="M120 100 L180 100" stroke="#333" strokeWidth="15" strokeLinecap="round"/>
                    <text x="150" y="170" textAnchor="middle" fill="#333" fontWeight="bold">BLOCKADE ACTION</text>
                    <text x="150" y="30" textAnchor="middle" fill="#555" fontSize="14">Conventional Drug</text>
                  </svg>
                </div>
                <p className="mb-4"><strong>Metaphor:</strong> Like using a sledgehammer to turn off a light switch.</p>
                <p>
                  Traditional pharmaceuticals often work by completely blocking a receptor or 
                  shutting down an enzyme. This can effectively stop a problem (like pain or 
                  inflammation) but may disrupt related pathways, leading to side effects.
                </p>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="bg-[#3498DB] p-3">
                <h4 className="font-serif text-lg font-semibold text-white">Peptide Therapeutic</h4>
              </div>
              <div className="p-6">
                <div className="flex justify-center mb-6">
                  <svg className="h-48 w-full" viewBox="0 0 300 200">
                    <rect x="70" y="50" width="160" height="100" rx="10" fill="#f5f5f5" stroke="#d1d1d1" strokeWidth="2"/>
                    <path d="M120,100 Q150,70 180,100" stroke="#3498DB" strokeWidth="5" fill="none"/>
                    <path d="M120,100 Q150,130 180,100" stroke="#3498DB" strokeWidth="5" fill="none"/>
                    <circle cx="120" cy="100" r="8" fill="#3498DB"/>
                    <circle cx="180" cy="100" r="8" fill="#3498DB"/>
                    <text x="150" y="170" textAnchor="middle" fill="#333" fontWeight="bold">MODULATION ACTION</text>
                    <text x="150" y="30" textAnchor="middle" fill="#555" fontSize="14">Peptide Therapy</text>
                  </svg>
                </div>
                <p className="mb-4"><strong>Metaphor:</strong> Like having a conversation with a cell in its own language.</p>
                <p>
                  Peptides typically work by mimicking the body's natural signals. Rather than 
                  shutting down a system, they can dial it up or down, allowing for more nuanced 
                  regulation of biological processes.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-[#ECF0F1] p-6 rounded-lg">
            <h3 className="font-serif text-xl font-bold text-[#2C3E50] mb-3">Clinical Implications</h3>
            <p className="mb-4">The biomimetic approach of peptides offers several potential advantages in clinical settings:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Reduced risk of adverse reactions</li>
              <li>Better tolerability in long-term treatment</li>
              <li>More physiological responses</li>
              <li>Potential for combination therapies with fewer drug interactions</li>
            </ul>
          </div>
        </div>
      )
    },
    
    // Peptide History
    {
      title: "History of Peptide Therapy",
      content: (
        <div>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#2C3E50] mb-8 pb-2 border-b-2 border-[#ECF0F1]">
            History of Peptide Therapy
          </h2>
          
          <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-10">
            <div className="p-6">
              <div className="flex justify-between items-start mb-8">
                <h3 className="font-serif text-2xl font-bold text-[#2C3E50]">
                  From Nobel Prize to GLP-1 Revolution
                </h3>
                <div className="px-3 py-1 bg-[#3498DB] text-white text-sm rounded-full">
                  100+ Years of Progress
                </div>
              </div>
              
              <div className="mb-8">
                <p className="mb-4">
                  The scientific journey of peptide therapeutics spans over a century, with several key milestones 
                  that have shaped modern medicine. This timeline highlights how peptides evolved from scientific 
                  curiosities to mainstream medical treatments.
                </p>
                
                <KeyTakeaway>
                  The peptide timeline illustrates a fundamental progression in medical science: from simply replacing missing 
                  natural hormones (insulin) to engineering novel peptides that target specific biological pathways with 
                  unprecedented precision.
                </KeyTakeaway>
              </div>
              
              <div className="bg-[#F8F9FA] p-6 rounded-lg mb-8">
                <h4 className="font-bold text-xl text-[#2C3E50] mb-4">
                  The Peptide Development Timeline
                </h4>
                
                <div className="relative ml-4 pl-6 border-l-2 border-[#3498DB]">
                  <div className="absolute left-0 top-1 w-4 h-4 rounded-full bg-[#3498DB] -ml-2"></div>
                  <div className="mb-6">
                    <h5 className="font-bold mb-1">1921-1922: Insulin – The First Peptide Therapy</h5>
                    <p className="mb-2">
                      Frederick Banting and Charles Best discovered insulin in 1921. The first patient was treated 
                      in 1922—a 14-year-old boy near death from diabetes. He recovered rapidly after receiving 
                      insulin injections.
                    </p>
                    <p className="text-sm text-[#E74C3C] font-semibold">
                      Impact: Revolutionized diabetes treatment; established peptides as viable therapeutics
                    </p>
                  </div>
                  
                  <div className="absolute left-0 top-32 w-4 h-4 rounded-full bg-[#3498DB] -ml-2"></div>
                  <div className="mb-6">
                    <h5 className="font-bold mb-1">1950s-1960s: Oxytocin and Vasopressin</h5>
                    <p className="mb-2">
                      Vincent du Vigneaud synthesized oxytocin in 1953, marking the first laboratory synthesis of a 
                      peptide hormone. This achievement earned him the 1955 Nobel Prize in Chemistry and opened the 
                      door to synthetic peptide development.
                    </p>
                    <p className="text-sm text-[#E74C3C] font-semibold">
                      Impact: Demonstrated that peptide hormones could be artificially produced
                    </p>
                  </div>
                  
                  <div className="absolute left-0 top-64 w-4 h-4 rounded-full bg-[#3498DB] -ml-2"></div>
                  <div className="mb-6">
                    <h5 className="font-bold mb-1">1970s-1980s: The Expansion Era</h5>
                    <p className="mb-2">
                      Scientists developed numerous peptide therapies during this period, including:
                    </p>
                    <ul className="list-disc ml-5 mb-2 grid grid-cols-2 gap-2">
                      <li><strong>Calcitonin</strong> for osteoporosis</li>
                      <li><strong>Leuprolide</strong> for prostate cancer</li>
                      <li><strong>Octreotide</strong> for endocrine disorders</li>
                      <li><strong>Enfuvirtide</strong> for HIV (later, in 2003)</li>
                    </ul>
                    <p className="text-sm text-[#E74C3C] font-semibold">
                      Impact: Established peptides as a diverse therapeutic class with applications across multiple medical fields
                    </p>
                  </div>
                  
                  <div className="absolute left-0 top-96 w-4 h-4 rounded-full bg-[#3498DB] -ml-2"></div>
                  <div className="mb-0">
                    <h5 className="font-bold mb-1">2000s-Present: The GLP-1 Revolution</h5>
                    <p className="mb-2">
                      The most talked-about new drugs for obesity and diabetes are GLP-1 (Glucagon-Like Peptide 1) 
                      analog peptides. In 2005, exenatide (Byetta®) became the first GLP-1 agonist approved for type 2 
                      diabetes. More recent GLP-1 analogs include semaglutide (Ozempic®, Wegovy®) and tirzepatide 
                      (Mounjaro®), which have shown dramatic weight loss benefits.
                    </p>
                    <p className="text-sm text-[#E74C3C] font-semibold">
                      Impact: Mainstream adoption of peptide therapies; transformative effects on obesity and diabetes treatment
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-[#EBF5FB] p-5 rounded-lg">
                <h4 className="font-bold text-[#2C3E50] mb-3">Today's Peptide Landscape</h4>
                <p className="mb-2">
                  Since insulin's debut, over 80 peptide drugs have been approved by the FDA. Today, more than 
                  150 peptide drugs are in active clinical development, and over 400 are in advanced preclinical phases. 
                  "Peptides" no longer refers to a single drug but to an entire therapeutic modality with applications 
                  across many medical specialties.
                </p>
                <div className="bg-white p-3 rounded">
                  <p className="text-sm italic">
                    Note: While this timeline focuses on therapeutic peptides, many other peptides have been 
                    discovered and characterized for their roles in biological systems without yet being 
                    developed into approved medications.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    
    // Current Landscape
    {
      title: "Current Landscape of Peptide Therapeutics",
      content: (
        <div>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#2C3E50] mb-8 pb-2 border-b-2 border-[#ECF0F1]">
            Current Landscape of Peptide Therapeutics
          </h2>
          
          <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-10">
            <div className="p-6">
              <h3 className="font-serif text-2xl font-bold text-[#2C3E50] mb-6">
                Market Overview & Growth Projections
              </h3>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                <div>
                  <p className="mb-6">
                    The peptide therapeutics market has evolved from a niche area to a major segment of the 
                    pharmaceutical industry. What was once viewed as an interesting but commercially limited 
                    field has become one of the fastest-growing sectors in drug development.
                  </p>
                  
                  <div className="bg-[#EBF5FB] p-5 rounded-lg mb-6">
                    <h4 className="font-bold text-[#2C3E50] mb-3">Current Market Status</h4>
                    <ul className="space-y-2">
                      <li className="flex items-start">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#3498DB] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>Worldwide sales of peptide drugs were about $20 billion in 2017</span>
                      </li>
                      <li className="flex items-start">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#3498DB] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>Projected to exceed $50 billion by 2024</span>
                      </li>
                      <li className="flex items-start">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#3498DB] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>Over 80 approved peptide drugs on the market</span>
                      </li>
                      <li className="flex items-start">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#3498DB] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>150+ peptides in active clinical trials</span>
                      </li>
                      <li className="flex items-start">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#3498DB] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>400+ in advanced preclinical development</span>
                      </li>
                    </ul>
                  </div>
                  
                  <p>
                    The rapid growth of this market is fueled by innovations in synthesis and delivery technologies, 
                    expanding applications in new therapeutic areas, and the commercial success of peptide blockbusters 
                    like semaglutide and liraglutide.
                  </p>
                </div>
                
                <div>
                  <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
                    <div className="bg-[#2C3E50] text-white p-3">
                      <h4 className="font-bold">Market Growth Projection</h4>
                    </div>
                    <div className="p-4">
                      <div className="relative h-64 bg-[#F8F9FA] p-4 rounded-lg flex items-center justify-center">
                        <svg viewBox="0 0 400 250" width="100%" height="100%">
                          <rect x="50" y="30" width="320" height="180" fill="none" stroke="#E0E0E0" strokeWidth="1" />
                          
                          {/* Grid lines */}
                          <line x1="50" y1="66" x2="370" y2="66" stroke="#E0E0E0" strokeWidth="1" />
                          <line x1="50" y1="102" x2="370" y2="102" stroke="#E0E0E0" strokeWidth="1" />
                          <line x1="50" y1="138" x2="370" y2="138" stroke="#E0E0E0" strokeWidth="1" />
                          <line x1="50" y1="174" x2="370" y2="174" stroke="#E0E0E0" strokeWidth="1" />
                          
                          <line x1="130" y1="30" x2="130" y2="210" stroke="#E0E0E0" strokeWidth="1" />
                          <line x1="210" y1="30" x2="210" y2="210" stroke="#E0E0E0" strokeWidth="1" />
                          <line x1="290" y1="30" x2="290" y2="210" stroke="#E0E0E0" strokeWidth="1" />
                          
                          {/* X-axis */}
                          <line x1="50" y1="210" x2="370" y2="210" stroke="#333" strokeWidth="2" />
                          
                          {/* Y-axis */}
                          <line x1="50" y1="30" x2="50" y2="210" stroke="#333" strokeWidth="2" />
                          
                          {/* X-axis labels */}
                          <text x="90" y="230" textAnchor="middle" fontSize="12" fill="#555">2017</text>
                          <text x="170" y="230" textAnchor="middle" fontSize="12" fill="#555">2020</text>
                          <text x="250" y="230" textAnchor="middle" fontSize="12" fill="#555">2022</text>
                          <text x="330" y="230" textAnchor="middle" fontSize="12" fill="#555">2024</text>
                          
                          {/* Y-axis labels */}
                          <text x="45" y="210" textAnchor="end" fontSize="12" fill="#555">$0</text>
                          <text x="45" y="174" textAnchor="end" fontSize="12" fill="#555">$10B</text>
                          <text x="45" y="138" textAnchor="end" fontSize="12" fill="#555">$20B</text>
                          <text x="45" y="102" textAnchor="end" fontSize="12" fill="#555">$35B</text>
                          <text x="45" y="66" textAnchor="end" fontSize="12" fill="#555">$45B</text>
                          <text x="45" y="30" textAnchor="end" fontSize="12" fill="#555">$60B</text>
                          
                          {/* Market growth line */}
                          <path d="M90,138 L170,120 L250,84 L330,48" stroke="#3498DB" strokeWidth="3" fill="none" />
                          
                          {/* Data points */}
                          <circle cx="90" cy="138" r="4" fill="#3498DB" />
                          <circle cx="170" cy="120" r="4" fill="#3498DB" />
                          <circle cx="250" cy="84" r="4" fill="#3498DB" />
                          <circle cx="330" cy="48" r="4" fill="#3498DB" />
                          
                          {/* Title */}
                          <text x="210" y="20" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#333">Global Peptide Therapeutics Market</text>
                        </svg>
                      </div>
                      <p className="text-sm text-center mt-2 text-gray-600">
                        Projected compound annual growth rate (CAGR) of approximately 9.8%
                      </p>
                    </div>
                  </div>
                  
                  <div className="bg-[#ECF0F1] p-5 rounded-lg">
                    <h4 className="font-bold text-[#2C3E50] mb-3">Key Market Trends</h4>
                    <ul className="space-y-2">
                      <li className="flex items-start">
                        <div className="h-5 w-5 mr-2 text-[#2C3E50] flex-shrink-0 mt-0.5 bg-[#3498DB] rounded-full flex items-center justify-center text-white font-bold text-xs">
                          1
                        </div>
                        <span><strong>Technological innovations</strong> in peptide design and delivery</span>
                      </li>
                      <li className="flex items-start">
                        <div className="h-5 w-5 mr-2 text-[#2C3E50] flex-shrink-0 mt-0.5 bg-[#3498DB] rounded-full flex items-center justify-center text-white font-bold text-xs">
                          2
                        </div>
                        <span><strong>Rising investments</strong> in peptide-focused biotechnology</span>
                      </li>
                      <li className="flex items-start">
                        <div className="h-5 w-5 mr-2 text-[#2C3E50] flex-shrink-0 mt-0.5 bg-[#3498DB] rounded-full flex items-center justify-center text-white font-bold text-xs">
                          3
                        </div>
                        <span><strong>Expanding applications</strong> beyond traditional endocrine disorders</span>
                      </li>
                      <li className="flex items-start">
                        <div className="h-5 w-5 mr-2 text-[#2C3E50] flex-shrink-0 mt-0.5 bg-[#3498DB] rounded-full flex items-center justify-center text-white font-bold text-xs">
                          4
                        </div>
                        <span><strong>Broader physician adoption</strong> across medical specialties</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <h3 className="font-serif text-2xl font-bold text-[#2C3E50] mb-6">
                Challenges & Innovations
              </h3>
              
              <div className="bg-white rounded-lg shadow-md overflow-hidden mb-8">
                <div className="bg-[#2C3E50] text-white p-3">
                  <h4 className="font-bold">Traditional Challenges & Cutting-Edge Solutions</h4>
                </div>
                <div className="p-4 overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-[#EBF5FB]">
                        <th className="py-3 px-4 text-left border-b-2 border-[#3498DB]">Challenge</th>
                        <th className="py-3 px-4 text-left border-b-2 border-[#3498DB]">Historical Limitation</th>
                        <th className="py-3 px-4 text-left border-b-2 border-[#3498DB]">Modern Innovations</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="even:bg-gray-50">
                        <td className="py-3 px-4 border-b border-gray-200 font-semibold">Oral Bioavailability</td>
                        <td className="py-3 px-4 border-b border-gray-200">Peptides historically could not be taken orally due to digestive degradation</td>
                        <td className="py-3 px-4 border-b border-gray-200">
                          <ul className="list-disc pl-5">
                            <li>Cyclization for stability</li>
                            <li>Lipid modifications</li>
                            <li>Carrier systems</li>
                            <li>SNAC technology (used in oral semaglutide)</li>
                          </ul>
                        </td>
                      </tr>
                      <tr className="even:bg-gray-50">
                        <td className="py-3 px-4 border-b border-gray-200 font-semibold">Short Half-life</td>
                        <td className="py-3 px-4 border-b border-gray-200">Rapid elimination from the body requiring frequent dosing</td>
                        <td className="py-3 px-4 border-b border-gray-200">
                          <ul className="list-disc pl-5">
                            <li>PEGylation</li>
                            <li>D-amino acid substitution</li>
                            <li>Albumin binding domains</li>
                            <li>Depot formulations</li>
                          </ul>
                        </td>
                      </tr>
                      <tr className="even:bg-gray-50">
                        <td className="py-3 px-4 border-b border-gray-200 font-semibold">Manufacturing Costs</td>
                        <td className="py-3 px-4 border-b border-gray-200">Expensive, complex synthesis processes</td>
                        <td className="py-3 px-4 border-b border-gray-200">
                          <ul className="list-disc pl-5">
                            <li>Recombinant production technologies</li>
                            <li>Solid-phase synthesis improvements</li>
                            <li>Scaled manufacturing processes</li>
                            <li>AI-driven design optimization</li>
                          </ul>
                        </td>
                      </tr>
                      <tr className="even:bg-gray-50">
                        <td className="py-3 px-4 font-semibold">Tissue Targeting</td>
                        <td className="py-3 px-4">Difficulty in reaching specific tissue targets</td>
                        <td className="py-3 px-4">
                          <ul className="list-disc pl-5">
                            <li>Cell-penetrating peptides</li>
                            <li>Tissue-specific delivery systems</li>
                            <li>Antibody-peptide conjugates</li>
                            <li>Nanoparticle formulations</li>
                          </ul>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              
              <KeyTakeaway>
                The technological barriers that historically limited peptide therapeutics are falling rapidly. Innovations in 
                stability, delivery, and manufacturing are expanding the range of conditions that can be addressed with 
                peptide-based approaches, heralding a new era in precision medicine.
              </KeyTakeaway>
            </div>
          </div>
        </div>
      )
    },
    
    // Applications - Part 1
    {
      title: "Key Applications of Peptide Therapeutics (1/2)",
      content: (
        <div>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#2C3E50] mb-8 pb-2 border-b-2 border-[#ECF0F1]">
            Key Applications of Peptide Therapeutics
          </h2>
          
          <p className="text-lg mb-8">
            The versatility of peptides makes them valuable across an exceptional range of 
            medical applications. Here we explore major areas where peptides are making 
            significant clinical impacts.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="bg-[#3498DB] h-2"></div>
              <div className="p-5">
                <h3 className="text-xl font-bold text-[#2C3E50] mb-3">Metabolic Health</h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#3498DB] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>GLP-1 agonists for diabetes and obesity</span>
                  </li>
                  <li className="flex items-start">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#3498DB] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Insulin analogs with improved profiles</span>
                  </li>
                  <li className="flex items-start">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#3498DB] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Growth hormone modulators</span>
                  </li>
                </ul>
                <p className="mt-4 p-3 bg-[#EBF5FB] rounded text-sm">
                  <strong>Highlight:</strong> GLP-1 agonists can produce 15-20% weight loss in clinical trials—comparable to bariatric surgery in some patients.
                </p>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="bg-[#E74C3C] h-2"></div>
              <div className="p-5">
                <h3 className="text-xl font-bold text-[#2C3E50] mb-3">Tissue Repair & Regeneration</h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#E74C3C] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>BPC-157 for tissue healing</span>
                  </li>
                  <li className="flex items-start">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#E74C3C] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>GHK-Cu for skin regeneration</span>
                  </li>
                  <li className="flex items-start">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#E74C3C] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Thymosin beta-4 for wound healing</span>
                  </li>
                </ul>
                <p className="mt-4 p-3 bg-[#EBF5FB] rounded text-sm">
                  <strong>Highlight:</strong> Some peptides can accelerate tendon and ligament healing by 25-30% in animal models, with promising early human data.
                </p>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="bg-[#2ECC71] h-2"></div>
              <div className="p-5">
                <h3 className="text-xl font-bold text-[#2C3E50] mb-3">Immune & Inflammation</h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#2ECC71] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Thymosin alpha-1 for immune enhancement</span>
                  </li>
                  <li className="flex items-start">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#2ECC71] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>KPV for anti-inflammatory effects</span>
                  </li>
                  <li className="flex items-start">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#2ECC71] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>LL-37 and other antimicrobial peptides</span>
                  </li>
                </ul>
                <p className="mt-4 p-3 bg-[#EBF5FB] rounded text-sm">
                  <strong>Highlight:</strong> Some antimicrobial peptides can overcome bacterial resistance mechanisms that defeat conventional antibiotics.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-10">
            <div className="bg-[#2C3E50] text-white p-4">
              <h3 className="font-serif text-xl font-semibold">Additional Therapeutic Areas</h3>
            </div>
            
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-[#F8F9FA] p-4 rounded-lg">
                <h4 className="font-bold text-[#2C3E50] mb-2">Neurology</h4>
                <ul className="space-y-1 text-sm">
                  <li>Neuroprotective peptides</li>
                  <li>Blood-brain barrier penetration</li>
                  <li>Cognitive enhancement</li>
                  <li>Pain management</li>
                </ul>
              </div>
              
              <div className="bg-[#F8F9FA] p-4 rounded-lg">
                <h4 className="font-bold text-[#2C3E50] mb-2">Oncology</h4>
                <ul className="space-y-1 text-sm">
                  <li>Targeted cancer therapies</li>
                  <li>Radiopharmaceutical carriers</li>
                  <li>Immune checkpoint modulators</li>
                  <li>Anti-angiogenic peptides</li>
                </ul>
              </div>
              
              <div className="bg-[#F8F9FA] p-4 rounded-lg">
                <h4 className="font-bold text-[#2C3E50] mb-2">Cardiovascular</h4>
                <ul className="space-y-1 text-sm">
                  <li>Natriuretic peptides</li>
                  <li>Antihypertensive therapies</li>
                  <li>Thrombolytic agents</li>
                  <li>Vascular repair peptides</li>
                </ul>
              </div>
              
              <div className="bg-[#F8F9FA] p-4 rounded-lg">
                <h4 className="font-bold text-[#2C3E50] mb-2">Dermatology</h4>
                <ul className="space-y-1 text-sm">
                  <li>Collagen-stimulating peptides</li>
                  <li>Melanocyte regulators</li>
                  <li>Anti-scarring formulations</li>
                  <li>Anti-aging peptides</li>
                </ul>
              </div>
            </div>
            
            <div className="p-6 pt-0">
              <KeyTakeaway>
                The versatility of peptides makes them applicable across virtually every medical specialty—from 
                endocrinology and gastroenterology to dermatology, neurology, and oncology. As our understanding 
                of cellular communication pathways deepens, the therapeutic potential of peptides continues to expand.
              </KeyTakeaway>
            </div>
          </div>
        </div>
      )
    },
    
    // Applications - Part 2
    {
      title: "Key Applications of Peptide Therapeutics (2/2)",
      content: (
        <div>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#2C3E50] mb-8 pb-2 border-b-2 border-[#ECF0F1]">
            Beyond Medical Applications
          </h2>
          
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-[#3498DB] to-[#2C3E50] text-white p-4">
              <h3 className="font-serif text-xl font-semibold">Research & Diagnostic Uses</h3>
            </div>
            
            <div className="p-6">
              <p className="mb-6">
                Peptides aren't limited to therapeutic uses. They serve as valuable tools in research and diagnostics:
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-[#F8F9FA] p-4 rounded-lg">
                  <h4 className="font-bold text-[#2C3E50] mb-2">Research Applications</h4>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <div className="h-5 w-5 mr-2 bg-[#3498DB] text-white flex-shrink-0 mt-0.5 rounded-full flex items-center justify-center font-bold text-xs">
                        R
                      </div>
                      <span><strong>Receptor mapping</strong> to understand signaling pathways</span>
                    </li>
                    <li className="flex items-start">
                      <div className="h-5 w-5 mr-2 bg-[#3498DB] text-white flex-shrink-0 mt-0.5 rounded-full flex items-center justify-center font-bold text-xs">
                        R
                      </div>
                      <span><strong>Cell-penetrating peptides</strong> for laboratory delivery of biomolecules</span>
                    </li>
                    <li className="flex items-start">
                      <div className="h-5 w-5 mr-2 bg-[#3498DB] text-white flex-shrink-0 mt-0.5 rounded-full flex items-center justify-center font-bold text-xs">
                        R
                      </div>
                      <span><strong>Molecular probes</strong> for cellular imaging</span>
                    </li>
                  </ul>
                </div>
                
                <div className="bg-[#F8F9FA] p-4 rounded-lg">
                  <h4 className="font-bold text-[#2C3E50] mb-2">Diagnostic Applications</h4>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <div className="h-5 w-5 mr-2 bg-[#3498DB] text-white flex-shrink-0 mt-0.5 rounded-full flex items-center justify-center font-bold text-xs">
                        D
                      </div>
                      <span><strong>Radiopharmaceutical carriers</strong> for enhanced imaging</span>
                    </li>
                    <li className="flex items-start">
                      <div className="h-5 w-5 mr-2 bg-[#3498DB] text-white flex-shrink-0 mt-0.5 rounded-full flex items-center justify-center font-bold text-xs">
                        D
                      </div>
                      <span><strong>Biomarker detection</strong> in sensitive assays</span>
                    </li>
                    <li className="flex items-start">
                      <div className="h-5 w-5 mr-2 bg-[#3498DB] text-white flex-shrink-0 mt-0.5 rounded-full flex items-center justify-center font-bold text-xs">
                        D
                      </div>
                      <span><strong>Point-of-care diagnostics</strong> based on peptide-antigen interactions</span>
                    </li>
                  </ul>
                </div>
              </div>
              
              <p>
                These non-therapeutic applications often drive innovations that eventually translate to clinical 
                peptide therapies, creating a virtuous cycle of discovery and advancement.
              </p>
            </div>
          </div>
        </div>
      )
    },
    
    // Regulatory Landscape - FDA Actions
    {
      title: "Regulatory Landscape: FDA Actions",
      content: (
        <div>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#2C3E50] mb-8 pb-2 border-b-2 border-[#ECF0F1]">
            Regulatory Landscape: FDA Actions
          </h2>
          
          <div className="mb-6">
            <h3 className="font-serif text-2xl font-bold text-[#2C3E50] mb-4">Recent FDA Regulatory Actions</h3>
            
            <div className="mb-4 border-l-4 border-blue-500 pl-4 py-3 bg-blue-50">
              <h4 className="font-semibold mb-1">2019-2020: Peptide vs. Protein Reclassification</h4>
              <p className="mb-2">
                In 2019, the FDA refined the definition of peptides vs. proteins. They ruled that any amino acid 
                polymer longer than 40 amino acids is considered a "protein" (thus a biologic drug), whereas 
                40 or fewer amino acids is a "peptide."
              </p>
              <p className="text-sm text-gray-600">
                This matters because under U.S. law, biologics cannot be compounded in regular pharmacies. 
                By March 2020, this meant that certain longer peptides (like Tesamorelin, a 44-aa peptide) 
                suddenly became classified as biologics, so compounding pharmacies had to stop making them.
              </p>
            </div>
            
            <div className="mb-6 border-l-4 border-red-500 pl-4 py-3 bg-red-50">
              <h4 className="font-semibold mb-1">November 2023: Compounded Peptides Ban</h4>
              <p className="mb-2">
                In November 2023, the FDA took further action specifically targeting commonly compounded peptides. 
                They banned compounding pharmacies from making or selling 17 popular peptides, including:
              </p>
              <ul className="list-disc pl-5 space-y-1 mb-2">
                <li>BPC-157</li>
                <li>Thymosin Alpha-1</li>
                <li>CJC-1295</li>
                <li>Ipamorelin</li>
                <li>Selank</li>
                <li>Semax</li>
                <li>And others</li>
              </ul>
              <p className="text-sm text-gray-600">
                The FDA's reasoning was that there is insufficient safety and efficacy data in humans, 
                concerns about product quality, and some reports of adverse effects for certain peptides.
              </p>
            </div>
            
            <div className="bg-gray-100 rounded-lg p-5">
              <h4 className="font-semibold mb-2">Impact on Access</h4>
              <p className="mb-3">
                These regulatory moves have pushed more of the peptide supply chain into the shadows:
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  Doctors who were prescribing (and supervising use of) peptides lost legal access to many of them
                </li>
                <li>
                  Patients interested in those peptides now often turn to the "research chemical" market
                </li>
                <li>
                  Compounding pharmacies were initially allowed to dispense remaining inventory, but moving 
                  forward they can't compound those banned substances
                </li>
              </ul>
            </div>
          </div>
          
          <KeyTakeaway>
            The regulatory environment for peptides is in flux – peptides are in a gray area where demand is high, 
            but official approval (and thus legitimate supply) is lagging behind. This has created a situation 
            where innovation and access are balanced against safety and oversight concerns.
          </KeyTakeaway>
        </div>
      )
    },
    
    // Political Support and Future Outlook
    {
      title: "Political Support and Future Outlook",
      content: (
        <div>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#2C3E50] mb-8 pb-2 border-b-2 border-[#ECF0F1]">
            Political Support and Future Outlook
          </h2>
          
          <div className="flex flex-col md:flex-row gap-6 mb-6">
            <div className="md:w-2/3">
              <h3 className="font-serif text-2xl font-bold text-[#2C3E50] mb-4">Growing Political Attention</h3>
              <p className="mb-4">
                Peptides have caught the attention of policymakers and public figures who advocate 
                for medical freedom and innovation. Notably, Robert F. Kennedy Jr. (who has been vocal 
                on health topics) has criticized the FDA for restricting access to peptides.
              </p>
              <p className="mb-4">
                In one public statement, RFK Jr. decried <em>"the FDA's aggressive suppression of … peptides, 
                stem cells, [and] hyperbaric therapies"</em> that could benefit health but aren't patentable. 
                This support from political figures suggests there may be growing pressure to create legal 
                pathways for peptide treatments.
              </p>
              <p>
                If such political will translates to policy changes, we could see moves similar to 
                "Right to Try" laws, or pressure on the FDA to approve certain peptides faster or 
                create special access programs.
              </p>
            </div>
            
            <div className="md:w-1/3">
              <div className="bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                <div className="bg-[#EBF5FB] p-3">
                  <h4 className="font-semibold text-center">RFK Jr. on Peptides</h4>
                </div>
                <div className="p-4">
                  <div className="bg-gray-100 p-3 rounded-lg mb-3">
                    <svg viewBox="0 0 24 24" className="h-6 w-6 text-blue-400 float-left mr-2">
                      <path fill="currentColor" d="M22.46 6c-.77.35-1.6.58-2.46.69.88-.53 1.56-1.37 1.88-2.38-.83.5-1.75.85-2.72 1.05C18.37 4.5 17.26 4 16 4c-2.35 0-4.27 1.92-4.27 4.29 0 .34.04.67.11.98C8.28 9.09 5.11 7.38 3 4.79c-.37.63-.58 1.37-.58 2.15 0 1.49.75 2.81 1.91 3.56-.71 0-1.37-.2-1.95-.5v.03c0 2.08 1.48 3.82 3.44 4.21a4.22 4.22 0 0 1-1.93.07 4.28 4.28 0 0 0 4 2.98 8.521 8.521 0 0 1-5.33 1.84c-.34 0-.68-.02-1.02-.06C3.44 20.29 5.7 21 8.12 21 16 21 20.33 14.46 20.33 8.79c0-.19 0-.37-.01-.56.84-.6 1.56-1.36 2.14-2.23z"/>
                    </svg>
                    <p className="text-sm italic">
                      "When elected, I will end the FDA's aggressive suppression of off-patent medicines 
                      including peptides, stem cells, hyperbaric therapies and other remedies that can't 
                      be patented by Pharma."
                    </p>
                  </div>
                  <p className="text-xs text-gray-500 text-center">
                    Political interest in peptide access is growing as public awareness increases
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-[#3498DB] to-[#2980B9] p-4 text-white">
                <h4 className="font-bold">Regulatory Grey Zone Continues</h4>
              </div>
              <div className="p-5">
                <p className="mb-4">
                  For now, peptides remain in a regulatory grey zone where they are neither fully legalized 
                  nor completely illegal. This creates challenges:
                </p>
                <ul className="list-disc pl-5 space-y-2">
                  <li>Quality control is inconsistent across sources</li>
                  <li>Research on clinical applications is underfunded relative to potential</li>
                  <li>Patients struggle to find knowledgeable practitioners</li>
                  <li>Insurance coverage is nearly nonexistent</li>
                </ul>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-[#2ECC71] to-[#27AE60] p-4 text-white">
                <h4 className="font-bold">Looking Forward: Potential Solutions</h4>
              </div>
              <div className="p-5">
                <p className="mb-4">
                  Several approaches could improve the regulatory landscape for peptides:
                </p>
                <ul className="list-disc pl-5 space-y-2">
                  <li>Creation of specialized approval pathways for peptide therapies</li>
                  <li>Development of quality standards specifically for peptide products</li>
                  <li>Expansion of research funding for clinical trials</li>
                  <li>Greater education for physicians about peptide therapies</li>
                </ul>
              </div>
            </div>
          </div>
          
          <KeyTakeaway>
            The political and regulatory future of peptides remains uncertain. Growing interest from patients, 
            practitioners, and now politicians suggests the landscape may evolve toward greater access, but 
            safety concerns and quality control issues must be addressed for sustainable progress.
          </KeyTakeaway>
        </div>
      )
    },
    

    
    // Conclusion - Should You Use Peptides?
    {
      title: "Should You Use Peptides?",
      content: (
        <div>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#2C3E50] mb-8 pb-2 border-b-2 border-[#ECF0F1]">
            Should You Use Peptides? The Informed Decision
          </h2>
          
          <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-10">
            <div className="p-6">
              <p className="mb-6">
                After exploring the science, history, applications, and current landscape of peptides, 
                we arrive at the fundamental question: <em>Should you use peptides?</em> The answer isn't 
                universal—it depends entirely on your individual circumstances, health objectives, 
                and approach to making medical decisions.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div>
                  <h3 className="font-serif text-xl font-bold text-[#2C3E50] mb-4">Personal Risk-Benefit Assessment</h3>
                  <p className="mb-4">
                    The decision to use peptides should be based on a thorough risk-benefit analysis tailored 
                    to your specific situation. Key considerations include:
                  </p>
                  <ul className="list-disc pl-5 space-y-2 mb-4">
                    <li><strong>Your health condition</strong> and whether conventional treatments have been effective</li>
                    <li><strong>Risk tolerance</strong> - comfort with experimental treatments with varying levels of evidence</li>
                    <li><strong>Potential benefits</strong> versus known and unknown risks</li>
                    <li><strong>Quality of available products</strong> and your ability to source reliable options</li>
                    <li><strong>Access to medical supervision</strong> from knowledgeable practitioners</li>
                  </ul>
                  <p>
                    Someone with a serious, treatment-resistant condition might reasonably accept higher risks 
                    than someone who's generally healthy but interested in optimization or prevention.
                  </p>
                </div>
                
                <div>
                  <h3 className="font-serif text-xl font-bold text-[#2C3E50] mb-4">Understanding the Science is Non-Negotiable</h3>
                  <p className="mb-4">
                    If you're considering peptides, understanding cellular mechanisms and signaling pathways is 
                    <strong> mandatory</strong>, not optional. Without this knowledge, you cannot:
                  </p>
                  <ul className="list-disc pl-5 space-y-2 mb-4">
                    <li>Evaluate the legitimacy of claims about specific peptides</li>
                    <li>Understand potential cross-effects with other medications or supplements</li>
                    <li>Assess whether reported side effects make mechanistic sense</li>
                    <li>Adapt protocols based on your body's responses</li>
                  </ul>
                  <p>
                    No "peptide cheat sheet" or generic protocol will serve you for long. The field evolves rapidly, 
                    and your body's specific responses need personalized consideration.
                  </p>
                </div>
              </div>
              
              <div className="bg-gradient-to-r from-[#EBF5FB] to-white p-5 rounded-lg mb-8">
                <h3 className="font-serif text-xl font-bold text-[#2C3E50] mb-3">Beyond the Information Gap</h3>
                <p className="mb-4">
                  The current state of peptide information presents serious challenges:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white p-4 rounded shadow-sm">
                    <h4 className="font-bold text-[#E74C3C] mb-2">Academic Research</h4>
                    <p className="text-sm">
                      Rigorous but often inaccessible to non-scientists, focused on mechanisms rather than practical applications
                    </p>
                  </div>
                  <div className="bg-white p-4 rounded shadow-sm">
                    <h4 className="font-bold text-[#E74C3C] mb-2">Clinical Guidance</h4>
                    <p className="text-sm">
                      Limited because many peptides lack formal approval, leaving physicians without standard protocols
                    </p>
                  </div>
                  <div className="bg-white p-4 rounded shadow-sm">
                    <h4 className="font-bold text-[#E74C3C] mb-2">Online Communities</h4>
                    <p className="text-sm">
                      Often based on anecdotal evidence, variable quality information, and simplistic protocols
                    </p>
                  </div>
                </div>
              </div>
              
              <KeyTakeaway>
                The decision to use peptides is inherently personal. If you choose to explore peptides, 
                do so with comprehensive education, reliable resources, thoughtful integration with your overall 
                health strategy, and preferably under informed medical supervision.
              </KeyTakeaway>
            </div>
          </div>
          
          {/* Next Generation Membership */}
          <div className="bg-gradient-to-r from-[#2C3E50] to-[#1A5276] text-white rounded-lg shadow-lg overflow-hidden mb-10">
            <div className="p-6">
              <div className="flex flex-col md:flex-row items-center gap-6 mb-6">
                <div className="md:w-2/3">
                  <h3 className="font-serif text-2xl font-bold mb-4">Next Generation Membership</h3>
                  <p className="mb-4">
                    For those seeking to navigate the complex world of peptides with confidence and cutting-edge information, 
                    I've created a comprehensive educational experience that goes beyond basic articles and forum discussions.
                  </p>
                  <p>
                    The Next Generation Membership provides the education, community, and tools needed to make truly 
                    informed decisions about peptides and other advanced health interventions.
                  </p>
                </div>
                <div className="md:w-1/3 flex justify-center">
                  <div className="bg-white text-[#2C3E50] rounded-lg p-5 text-center max-w-xs">
                    <div className="text-3xl font-bold mb-2">Join Now</div>
                    <p className="text-sm mb-3">First 100 members receive exclusive benefits</p>
                    <button className="bg-[#3498DB] hover:bg-[#2980B9] text-white py-2 px-6 rounded-full font-semibold transition-colors">
                      Learn More
                    </button>
                    <p className="text-sm mt-3 text-gray-600">
                      Visit <a href="https://www.nextgenerationmedicine.co" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-700 font-medium">www.nextgenerationmedicine.co</a>
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-white/10 rounded-lg p-5">
                  <h4 className="font-bold text-xl mb-3">Membership Benefits</h4>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#3498DB] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span><strong>Weekly interactive deep dives</strong> on peptides and other longevity topics</span>
                    </li>
                    <li className="flex items-start">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#3498DB] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span><strong>Members-only WhatsApp community</strong> for real-time discussion and support</span>
                    </li>
                    <li className="flex items-start">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#3498DB] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span><strong>Monthly Q&A sessions</strong> to address your specific questions</span>
                    </li>
                    <li className="flex items-start">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#3498DB] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span><strong>20% discount</strong> on select trusted products</span>
                    </li>
                  </ul>
                </div>
                
                <div className="bg-white/10 rounded-lg p-5">
                  <h4 className="font-bold text-xl mb-3">Exclusive for First 100 Members</h4>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#E74C3C] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      <span><strong>Biannual non-medical reviews</strong> of your diagnostic data</span>
                    </li>
                    <li className="flex items-start">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#E74C3C] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      <span><strong>Early access to proprietary analysis tools</strong> for wearable data, VO2max, and DEXA scans</span>
                    </li>
                    <li className="flex items-start">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#E74C3C] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      <span><strong>Priority access to new content</strong> and special events</span>
                    </li>
                  </ul>
                  
                  <div className="mt-4 p-3 bg-white/20 rounded">
                    <p className="text-sm italic">
                      <strong>Note:</strong> Diagnostic data reviews are for informational purposes only and do not constitute 
                      medical advice. They are designed to help you better understand your health metrics and identify 
                      potential areas for further investigation with your healthcare provider.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="mb-8">
                <h4 className="font-bold text-xl mb-4">AI-Powered Knowledge Base</h4>
                <p className="mb-4">
                  Our curriculum is continuously updated using a proprietary AI-powered knowledge base that digests 
                  the latest research in real-time. This means you're always getting the most current, evidence-based 
                  information—not outdated protocols or anecdotal advice.
                </p>
                <div className="bg-white/10 p-4 rounded">
                  <p className="text-sm font-semibold mb-2">The Next Generation Knowledge System includes:</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="bg-[#2980B9] p-3 rounded">
                      <p className="font-semibold text-center">Fundamental Science</p>
                    </div>
                    <div className="bg-[#2980B9] p-3 rounded">
                      <p className="font-semibold text-center">Clinical Applications</p>
                    </div>
                    <div className="bg-[#2980B9] p-3 rounded">
                      <p className="font-semibold text-center">Business Applications</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Comparison Chart */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="bg-[#2C3E50] text-white p-4">
              <h3 className="font-serif text-xl font-semibold">How We Compare</h3>
            </div>
            <div className="p-6">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-[#ECF0F1]">
                      <th className="p-3 text-left border">Features</th>
                      <th className="p-3 text-center border"><span className="font-bold text-[#2C3E50]">Next Generation Membership</span></th>
                      <th className="p-3 text-center border">Typical Health Influencer Communities</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="p-3 border font-semibold">Communication Platform</td>
                      <td className="p-3 border text-center">
                        <span className="flex items-center justify-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          Convenient WhatsApp Community
                        </span>
                      </td>
                      <td className="p-3 border text-center">
                        <span className="flex items-center justify-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          Fragmented Forums & Platforms
                        </span>
                      </td>
                    </tr>
                    <tr className="bg-[#F8F9FA]">
                      <td className="p-3 border font-semibold">Content Focus</td>
                      <td className="p-3 border text-center">
                        <span className="flex items-center justify-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          Comprehensive (Science + Clinical + Business)
                        </span>
                      </td>
                      <td className="p-3 border text-center">
                        <span className="flex items-center justify-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          Usually Clinical Only, Limited Science
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td className="p-3 border font-semibold">Content Depth</td>
                      <td className="p-3 border text-center">
                        <span className="flex items-center justify-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          Deep Mechanistic Understanding
                        </span>
                      </td>
                      <td className="p-3 border text-center">
                        <span className="flex items-center justify-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          Surface-Level "How To" Protocols
                        </span>
                      </td>
                    </tr>
                    <tr className="bg-[#F8F9FA]">
                      <td className="p-3 border font-semibold">Leader Background</td>
                      <td className="p-3 border text-center">
                        <span className="flex items-center justify-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          Harvard-Trained Physician + Business
                        </span>
                      </td>
                      <td className="p-3 border text-center">
                        <span className="flex items-center justify-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          Variable, Often Non-Medical Background
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )
    },
    
    // About The Author
    {
      title: "About Dr. Anant Vinjamoori",
      content: (
        <div>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#2C3E50] mb-8 pb-2 border-b-2 border-[#ECF0F1]">
            About Dr. Anant Vinjamoori
          </h2>
          
          <div className="flex flex-col md:flex-row gap-8 mb-10">
            <div className="md:w-3/5">
              
              <p className="mb-6">
                I bring a uniquely integrated lens to longevity medicine, combining clinical expertise with product 
                development and technology innovation. This multidisciplinary approach allows me to create solutions 
                that are both scientifically rigorous and practically useful.
              </p>
              
              <p className="mb-6">
                As the Chief Medical Officer at Modern Age, I built the first national-scale longevity clinic in the 
                United States, establishing protocols and clinical approaches that have shaped the industry. Prior to that,
                I served as Director of Product at Virta Health, where I led product strategy at what grew to become 
                a multi-billion dollar digital health company.
              </p>
              
              <p className="mb-6">
                My foundation as a Harvard-trained physician with clinical training at Harvard Medical School and 
                Brigham & Women's Hospital provides the medical expertise that informs all of my work. As a recognized 
                longevity leader, I regularly speak, educate, and advise leading organizations across the field.
              </p>
              
              <p className="mb-6">
                Through Next Generation Medicine, I've developed custom analytical platforms for DEXA scan interpretation, 
                VO2max assessments, wearables data, and continuous glucose monitoring that translate complex biometric data 
                into actionable insights—tools that are exclusively available to our members.
              </p>
            </div>
            
            <div className="md:w-2/5">

              
              <div className="bg-[#F8F9FA] rounded-lg p-6 shadow-sm">
                <h3 className="text-xl font-bold text-[#2C3E50] mb-4">Professional Highlights</h3>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="font-bold text-[#3498DB] mb-2">Education</h4>
                    <ul className="space-y-2">
                      <li className="flex items-start">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#3498DB] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>Harvard Medical School, MD</span>
                      </li>
                      <li className="flex items-start">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#3498DB] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>Harvard Business School, MBA</span>
                      </li>
                      <li className="flex items-start">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#3498DB] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>Brigham and Women's Hospital Residency</span>
                      </li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-bold text-[#3498DB] mb-2">Leadership</h4>
                    <ul className="space-y-2">
                      <li className="flex items-start">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#3498DB] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>Modern Age, Chief Medical Officer</span>
                      </li>
                      <li className="flex items-start">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#3498DB] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>Virta Health, Director of Product</span>
                      </li>
                      <li className="flex items-start">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#3498DB] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>Longevity Leader & Advisor</span>
                      </li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-bold text-[#3498DB] mb-2">Selected Clients & Partners</h4>
                    <ul className="space-y-2">
                      <li className="flex items-start">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#3498DB] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>Midi Health: Fastest growing telehealth platform in women's health</span>
                      </li>
                      <li className="flex items-start">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#3498DB] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>Superpower: Data-driven personalized longevity platform</span>
                      </li>
                      <li className="flex items-start">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-[#3498DB] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>Clean Market: Regional IV clinic expanding into longevity</span>
                      </li>
                    </ul>
                  </div>
                  
                  <div className="mt-8 p-5 bg-gradient-to-r from-[#E1F5FE] to-[#E1EEFF] rounded-lg text-center shadow-sm">
                    <h4 className="font-bold text-[#2C3E50] mb-2">Join Next Generation Medicine</h4>
                    <p className="text-[#4A5568] mb-4">Access interactive content and exclusive data analysis tools</p>
                    <a 
                      href="/" 
                      className="inline-block px-5 py-2 bg-[#3498DB] text-white font-semibold rounded-md shadow-sm hover:bg-[#2980B9] transition duration-150"
                    >
                      View Membership Details
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },

    // Next Generation Medicine Membership - Part 1
    {
      title: "Next Generation Medicine Membership (1/3)",
      content: (
        <div>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#2C3E50] mb-8 pb-2 border-b-2 border-[#ECF0F1]">
            Next Generation Medicine Membership
          </h2>
          
          <div className="bg-gradient-to-r from-[#2C3E50] to-[#1A5276] text-white rounded-lg shadow-lg overflow-hidden mb-10 p-6">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="md:w-2/3">
                <h3 className="font-serif text-2xl font-bold mb-4">The Most Comprehensive Longevity Education Resource</h3>
                <p className="mb-4 text-lg">
                  Next Generation Medicine is a unique membership program that bridges the gap between cutting-edge 
                  scientific research and practical clinical applications in longevity medicine.
                </p>
                <p className="text-lg">
                  Created by Dr. Anant Vinjamoori, MD, this program delivers expert-level education on peptides, 
                  senolytics, and other advanced therapeutic approaches with the scientific rigor and clinical 
                  context that's missing from most resources.
                </p>
              </div>
              <div className="md:w-1/3">
                <div className="bg-white text-[#2C3E50] rounded-lg p-6 text-center">
                  <div className="text-3xl font-bold mb-2">Join Now</div>
                  <div className="text-lg font-medium mb-4">$99/month</div>
                  <div className="text-sm mb-4 text-gray-600">Science-based education for practitioners and health enthusiasts</div>
                  <Button className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-2 px-6 rounded-full w-full">
                    Learn More
                  </Button>
                  <div className="text-sm mt-3 text-gray-600">
                    Visit <a href="https://www.nextgenerationmedicine.co" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-700 font-medium">www.nextgenerationmedicine.co</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    
    // Next Generation Medicine Membership - Part 2
    {
      title: "Next Generation Medicine Membership (2/3)",
      content: (
        <div>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#2C3E50] mb-8 pb-2 border-b-2 border-[#ECF0F1]">
            Why Next Generation Medicine?
          </h2>
          
          <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-8">
            <div className="p-6">
              <h3 className="font-serif text-2xl font-bold text-[#2C3E50] mb-6">
                The Unique Advantage of Our Approach
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h4 className="font-bold text-xl text-[#2C3E50] mb-3">Dr. Anant Vinjamoori, MD's Unique Perspective</h4>
                  <ul className="space-y-4">
                    <li className="flex items-start">
                      <div className="h-6 w-6 mr-3 bg-blue-500 text-white flex-shrink-0 rounded-full flex items-center justify-center">
                        1
                      </div>
                      <div>
                        <strong className="block text-[#2C3E50]">Deep Scientific Knowledge</strong>
                        <p className="text-gray-600">Extensive research background in molecular biology and cell signaling mechanisms</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <div className="h-6 w-6 mr-3 bg-blue-500 text-white flex-shrink-0 rounded-full flex items-center justify-center">
                        2
                      </div>
                      <div>
                        <strong className="block text-[#2C3E50]">Clinical Medicine Expertise</strong>
                        <p className="text-gray-600">Actively practices medicine with patients seeking advanced interventions</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <div className="h-6 w-6 mr-3 bg-blue-500 text-white flex-shrink-0 rounded-full flex items-center justify-center">
                        3
                      </div>
                      <div>
                        <strong className="block text-[#2C3E50]">Business Acumen</strong>
                        <p className="text-gray-600">Experience building health ventures and understanding market dynamics</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <div className="h-6 w-6 mr-3 bg-blue-500 text-white flex-shrink-0 rounded-full flex items-center justify-center">
                        4
                      </div>
                      <div>
                        <strong className="block text-[#2C3E50]">Builder Mentality</strong>
                        <p className="text-gray-600">Creates practical tools and frameworks that members can immediately apply</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <div className="h-6 w-6 mr-3 bg-blue-500 text-white flex-shrink-0 rounded-full flex items-center justify-center">
                        5
                      </div>
                      <div>
                        <strong className="block text-[#2C3E50]">AI Integration</strong>
                        <p className="text-gray-600">At the forefront of applying artificial intelligence to longevity medicine</p>
                      </div>
                    </li>
                  </ul>
                </div>
                
                <div>
                  <h4 className="font-bold text-xl text-[#2C3E50] mb-4">Comparison With Other Educators</h4>
                  <div className="bg-gray-50 p-4 rounded-lg mb-4">
                    <div className="flex items-center mb-3">
                      <div className="h-8 w-8 mr-3 bg-blue-500/10 text-blue-500 flex-shrink-0 rounded-full flex items-center justify-center font-bold">
                        NM
                      </div>
                      <div>
                        <span className="font-medium">Next Generation Medicine</span>
                      </div>
                    </div>
                    <p className="text-gray-600 mb-2">
                      <strong>Unique value:</strong> Deep scientific mechanisms + practical applications + AI-enhanced tools
                    </p>
                    <p className="text-gray-600">
                      <strong>Advantage:</strong> Spans the full spectrum from molecular detail to clinical implementation
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-1 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="font-medium mb-1">Other leading educators focus on:</p>
                      <ul className="list-disc pl-5 text-gray-600 space-y-1">
                        <li>Broad lifestyle approaches without molecular detail (Hyman)</li>
                        <li>Performance metrics without mechanistic depth (Attia)</li>
                        <li>Neuroscience without comprehensive integration (Huberman)</li>
                      </ul>
                    </div>
                    
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <p className="font-medium text-blue-800 mb-2">The Next Generation Medicine Difference:</p>
                      <p className="text-gray-700">
                        Unlike others in the space, Dr. Anant Vinjamoori, MD combines scientific precision with 
                        practical application while actively building new tools and technologies to 
                        enhance longevity medicine practice.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    
    // Next Generation Medicine Membership - Part 3
    {
      title: "Next Generation Medicine Membership (3/3)",
      content: (
        <div>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#2C3E50] mb-8 pb-2 border-b-2 border-[#ECF0F1]">
            Membership Benefits
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-blue-500 to-indigo-600 h-2"></div>
              <div className="p-6">
                <h3 className="font-serif text-xl font-bold text-[#2C3E50] mb-4">Educational Resources</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-blue-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span><strong>Interactive content</strong> with detailed visualizations</span>
                  </li>
                  <li className="flex items-start">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-blue-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span><strong>Weekly deep dives</strong> into peptides, senolytics, and more</span>
                  </li>
                  <li className="flex items-start">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-blue-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span><strong>Research analysis</strong> of the most important new studies</span>
                  </li>
                  <li className="flex items-start">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-blue-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span><strong>Case studies</strong> demonstrating real-world applications</span>
                  </li>
                  <li className="flex items-start">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-blue-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span><strong>Protocol libraries</strong> with detailed implementation guides</span>
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-purple-500 to-pink-500 h-2"></div>
              <div className="p-6">
                <h3 className="font-serif text-xl font-bold text-[#2C3E50] mb-4">Community & Support</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-purple-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span><strong>Private WhatsApp group</strong> for real-time connections</span>
                  </li>
                  <li className="flex items-start">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-purple-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span><strong>Monthly Q&A sessions</strong> with Dr. Anant Vinjamoori, MD</span>
                  </li>
                  <li className="flex items-start">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-purple-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span><strong>Expert interviews</strong> with leading researchers</span>
                  </li>
                  <li className="flex items-start">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-purple-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span><strong>Peer networking</strong> with like-minded professionals</span>
                  </li>
                  <li className="flex items-start">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-purple-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span><strong>Email support</strong> for member questions</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6 mb-8 border border-blue-100">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="md:w-2/3">
                <h3 className="font-serif text-xl font-bold text-[#2C3E50] mb-3">
                  Ready to Go Beyond Basic Peptide Information?
                </h3>
                <p className="text-gray-700 mb-4">
                  Join Next Generation Medicine today to gain access to the most comprehensive, science-based education 
                  on peptides and advanced longevity interventions. Dr. Anant Vinjamoori, MD's unique combination of scientific expertise, 
                  clinical experience, business knowledge, and technology integration provides unparalleled value.
                </p>
                <div className="flex items-center space-x-4">
                  <Button className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-2 px-6 rounded-full">
                    Join Now
                  </Button>
                  <Button variant="outline" className="border-blue-600 text-blue-600 hover:bg-blue-50 py-2 px-6 rounded-full">
                    Learn More
                  </Button>
                </div>
                <div className="mt-4 text-gray-700">
                  Visit <a href="https://www.nextgenerationmedicine.co" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 font-medium">www.nextgenerationmedicine.co</a> for more information
                </div>
              </div>
              <div className="md:w-1/3 flex justify-center">
                <div className="bg-white rounded-full p-2 shadow-lg">
                  <div 
                    className="w-36 h-36 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xl font-bold"
                  >
                    Dr. Anant
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header with navigation controls */}
      <div className="bg-white shadow-md py-2 px-4 flex justify-between items-center">
        <button 
          onClick={() => setLocation('/')}
          className="flex items-center gap-2 text-gray-700 hover:text-blue-600 transition-colors"
        >
          <Home size={20} />
          <span>Return to Home</span>
        </button>
        
        <div className="flex items-center gap-4">
          <button 
            onClick={toggleFullscreen}
            className="flex items-center gap-1 bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded-md transition-colors"
          >
            {isFullscreen ? (
              <>
                <Minimize size={16} />
                <span className="text-sm">Exit Fullscreen</span>
              </>
            ) : (
              <>
                <Maximize size={16} />
                <span className="text-sm">Fullscreen</span>
              </>
            )}
          </button>
          <div className="text-sm text-gray-500 flex items-center gap-2">
            Press <kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-xs">F</kbd> for fullscreen
            <span className="mx-1">•</span>
            <kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-xs">←</kbd>/<kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-xs">→</kbd> for navigation
          </div>
        </div>
      </div>
      
      {/* Slideshow container */}
      <div className="flex-1 p-4">
        <Carousel 
          className="w-full max-w-6xl mx-auto bg-white rounded-xl shadow-lg overflow-hidden"
          opts={{
            align: "start"
          }}
          setApi={setCarouselApi}
          onSelect={() => {
            if (carouselApi) {
              const selectedIndex = carouselApi.selectedScrollSnap() || 0;
              setActiveIndex(selectedIndex);
            }
          }}
        >
          <CarouselContent>
            {slides.map((slide, index) => (
              <CarouselItem key={index} className="flex flex-col">
                <div className="flex justify-between items-center p-4 border-b">
                  <div className="flex items-center gap-2">
                    <Presentation className="text-blue-500" size={20} />
                    <h1 className="font-medium">{slide.title}</h1>
                  </div>
                  <div className="text-sm text-gray-500">
                    Slide {index + 1} of {slides.length}
                  </div>
                </div>
                <div className="p-8" style={{height: 'calc(100vh - 170px)', overflow: 'auto'}}>
                  {slide.content}
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          
          <div className="absolute left-2 top-1/2 -translate-y-1/2 z-10">
            <CarouselPrevious className="h-12 w-12 bg-white/90 shadow-lg" />
          </div>
          <div className="absolute right-2 top-1/2 -translate-y-1/2 z-10">
            <CarouselNext className="h-12 w-12 bg-white/90 shadow-lg" />
          </div>
          
          <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2">
            {slides.map((_, index) => (
              <div 
                key={index}
                className={`h-2 rounded-full transition-all ${
                  index === activeIndex ? 'w-8 bg-blue-500' : 'w-2 bg-gray-300'
                }`}
              />
            ))}
          </div>
        </Carousel>
      </div>
    </div>
  );
}